import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [autoplayToggle]
 * @property {HTMLElement} [liveRegion]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.#handleFocusIn);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.#handleFocusIn);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
  }

  /**
   * Displays and announces the next slide after a user activates the control.
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.pause();
    this.current += 1;
    this.#announceCurrentSlide();
  }

  /**
   * Displays and announces the previous slide after a user activates the control.
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.pause();
    this.current -= 1;
    this.#announceCurrentSlide();
  }

  /**
   * Starts automatic slide playback.
   */
  play() {
    if (!this.autoplay || this.#interval) return;

    this.paused = false;
    if (this.refs.liveRegion instanceof HTMLElement) this.refs.liveRegion.textContent = '';

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.current += 1;
    }, this.autoplayInterval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
    this.#updateAutoplayControl();
  }

  /**
   * Toggles automatic playback from the visible rotation control.
   * @param {Event} event
   */
  toggleAutoplay(event) {
    event.preventDefault();
    this.paused ? this.play() : this.pause();
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || this.#interval) return;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    this.#current = current;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      if (index !== relativeIndex) slide.setAttribute('aria-hidden', 'true');
    });

    slides[relativeIndex]?.setAttribute('aria-hidden', 'false');
  }

  /** Announces only the current message after user-requested navigation. */
  #announceCurrentSlide() {
    const slides = this.refs.slides ?? [];
    const liveRegion = this.refs.liveRegion;
    if (!slides.length || !(liveRegion instanceof HTMLElement)) return;

    let relativeIndex = this.current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    const announcement = slides[relativeIndex]?.querySelector('.announcement-bar__text')?.textContent?.trim();
    if (announcement) liveRegion.textContent = announcement;
  }

  /** Keeps the toggle's accessible name and icon action in sync with playback. */
  #updateAutoplayControl() {
    const control = this.refs.autoplayToggle;
    if (!(control instanceof HTMLButtonElement)) return;

    const label = this.paused ? control.dataset.labelPlay : control.dataset.labelPause;
    if (label) control.setAttribute('aria-label', label);
  }

  /** Stops rotation when focus enters the carousel controls or slide content. */
  #handleFocusIn = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (event.target === this.refs.autoplayToggle) return;

    this.pause();
  };

  /**
   * Temporarily suspend the slideshow while the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
